import { useState, type ChangeEvent, type FormEvent } from "react";
import type { UserRole } from "./auth-types";
import { ConferenceBrandImage } from "./ConferenceBrandImage";
import {
  CONFERENCE_BRANDING_ACCEPT,
  canManageConferenceBranding,
  effectiveConferenceBrandingUrl,
  readConferenceBrandingDimensions,
  validateConferenceBrandingDimensions,
  validateConferenceBrandingFile,
  type ConferenceBrandingFileError,
} from "./conference-branding";
import type {
  ConferenceBrandingAsset,
  ConferenceBrandingAssetKind,
  ConferenceBrandingOverride,
  ConferenceDashboardClient,
  ConferenceDetail,
} from "./portfolio-overview-api";
import { ConferenceBrandingApiError } from "./portfolio-overview-api";
import { portfolioErrorMessageKey } from "./portfolio-api-errors";
import {
  isolateLtr,
  isolateText,
  useLocale,
  type MessageKey,
} from "./localization";

type BrandingSelections = Readonly<
  Record<ConferenceBrandingAssetKind, File | null>
>;
type BrandingPreviews = Readonly<
  Record<ConferenceBrandingAssetKind, string | null>
>;
type BrandingFeedback = Readonly<
  Record<ConferenceBrandingAssetKind, MessageKey | null>
>;

const initialSelections: BrandingSelections = Object.freeze({
  icon: null,
  logo: null,
});
const initialPreviews: BrandingPreviews = Object.freeze({
  icon: null,
  logo: null,
});
const initialFeedback: BrandingFeedback = Object.freeze({
  icon: null,
  logo: null,
});

type Translator = ReturnType<typeof useLocale>["t"];
type NumberFormatter = ReturnType<typeof useLocale>["number"];

function brandingLimitParameters(number: NumberFormatter) {
  return {
    maxMiB: isolateLtr(number(5)),
    minPixels: isolateLtr(number(16)),
    maxPixels: isolateLtr(number(4096)),
    maxMegapixels: isolateLtr(
      number(16.7, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    ),
    minAspect: isolateLtr(
      number(0.8, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    ),
    maxAspect: isolateLtr(
      number(1.25, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    ),
  } as const;
}

function assetLabel(kind: ConferenceBrandingAssetKind, t: Translator): string {
  return t(kind === "icon" ? "branding.asset.icon" : "branding.asset.logo");
}

function assetDescription(
  kind: ConferenceBrandingAssetKind,
  t: Translator,
): string {
  return kind === "icon"
    ? t("branding.asset.iconDescription")
    : t("branding.asset.logoDescription");
}

function currentAsset(
  conference: ConferenceDetail,
  kind: ConferenceBrandingAssetKind,
): ConferenceBrandingAsset | null {
  return conference.brandingOverride?.[kind] ?? null;
}

function versionSummary(
  asset: ConferenceBrandingAsset | null,
  t: Translator,
  number: ReturnType<typeof useLocale>["number"],
): string {
  if (!asset) return t("branding.source.connector");
  return t("branding.source.admin", {
    version: isolateLtr(asset.version),
    width: isolateLtr(number(asset.width)),
    height: isolateLtr(number(asset.height)),
  });
}

function fileErrorKey(code: ConferenceBrandingFileError): MessageKey {
  if (code === "file_too_large") return "branding.error.fileTooLarge";
  if (code === "unsupported_type") return "branding.error.unsupportedType";
  if (code === "icon_aspect_ratio") return "branding.error.iconAspect";
  if (code === "invalid_dimensions") {
    return "branding.error.invalidDimensions";
  }
  return "branding.error.emptyFile";
}

function mutationErrorKey(error: unknown): MessageKey {
  if (error instanceof ConferenceBrandingApiError) {
    if (error.code === "file_too_large") return "branding.error.uploadLimit";
    if (error.code === "invalid_image") return "branding.error.invalidImage";
    if (error.code === "conference_not_found") {
      return "branding.error.conferenceNotFound";
    }
    if (error.code === "storage_unavailable") {
      return "branding.error.storageUnavailable";
    }
  }
  return portfolioErrorMessageKey(error, "branding.error.saveFailed");
}

function BrandingAssetPreview({
  conference,
  kind,
  previewUrl,
}: {
  readonly conference: ConferenceDetail;
  readonly kind: ConferenceBrandingAssetKind;
  readonly previewUrl: string | null;
}) {
  const { t } = useLocale();
  const configuredLogo =
    previewUrl ?? effectiveConferenceBrandingUrl(conference, kind);
  return (
    <div
      className={`conference-branding__preview conference-branding__preview--${kind}`}
    >
      <ConferenceBrandImage
        conferenceId={conference.conferenceId}
        conferenceName={conference.name}
        configuredLogo={configuredLogo}
        compact={kind === "icon"}
        alt={t("branding.previewAlt", {
          conferenceName: isolateText(conference.name),
          asset: assetLabel(kind, t),
        })}
        fallbackClassName="conference-branding__monogram"
      />
    </div>
  );
}

export function ConferenceBrandingPanel({
  client,
  conference,
  role,
  onBrandingUpdated,
}: {
  readonly client: ConferenceDashboardClient;
  readonly conference: ConferenceDetail;
  readonly role: UserRole;
  readonly onBrandingUpdated: (
    brandingOverride: ConferenceBrandingOverride | null,
  ) => void;
}) {
  const { number, t } = useLocale();
  const [selections, setSelections] =
    useState<BrandingSelections>(initialSelections);
  const [previews, setPreviews] = useState<BrandingPreviews>(initialPreviews);
  const [feedback, setFeedback] = useState<BrandingFeedback>(initialFeedback);
  const [operation, setOperation] = useState<Readonly<{
    kind: ConferenceBrandingAssetKind;
    action: "upload" | "remove";
  }> | null>(null);
  const [inspectingKind, setInspectingKind] =
    useState<ConferenceBrandingAssetKind | null>(null);
  const isAdmin = canManageConferenceBranding(role);
  const limitParameters = brandingLimitParameters(number);

  function setKindFeedback(
    kind: ConferenceBrandingAssetKind,
    message: MessageKey | null,
  ) {
    setFeedback((current) => ({ ...current, [kind]: message }));
  }

  function clearSelection(kind: ConferenceBrandingAssetKind) {
    setSelections((current) => ({ ...current, [kind]: null }));
    setPreviews((current) => ({ ...current, [kind]: null }));
  }

  async function selectFile(
    kind: ConferenceBrandingAssetKind,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    if (!isAdmin || operation || inspectingKind) return;
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) return;
    const validationError = validateConferenceBrandingFile(file);
    if (validationError) {
      clearSelection(kind);
      setKindFeedback(kind, fileErrorKey(validationError));
      return;
    }

    setInspectingKind(kind);
    setKindFeedback(kind, "branding.checkingDimensions");
    try {
      const dimensions = await readConferenceBrandingDimensions(file);
      const dimensionError = validateConferenceBrandingDimensions(
        kind,
        dimensions,
      );
      if (dimensionError) {
        clearSelection(kind);
        setKindFeedback(kind, fileErrorKey(dimensionError));
        return;
      }
    } catch {
      clearSelection(kind);
      setKindFeedback(kind, "branding.error.dimensionsUnreadable");
      return;
    } finally {
      setInspectingKind(null);
    }

    setSelections((current) => ({ ...current, [kind]: file }));
    setKindFeedback(kind, null);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setPreviews((current) => ({ ...current, [kind]: reader.result }));
      }
    };
    reader.onerror = () => {
      clearSelection(kind);
      setKindFeedback(kind, "branding.error.previewUnavailable");
    };
    reader.readAsDataURL(file);
  }

  async function upload(
    kind: ConferenceBrandingAssetKind,
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    const image = selections[kind];
    if (!isAdmin || !image || operation || inspectingKind) return;
    setOperation({ kind, action: "upload" });
    setKindFeedback(kind, null);
    try {
      const brandingOverride = await client.uploadConferenceBranding(
        conference.conferenceId,
        kind,
        image,
      );
      onBrandingUpdated(brandingOverride);
      clearSelection(kind);
      setKindFeedback(kind, "branding.updated");
    } catch (error) {
      setKindFeedback(kind, mutationErrorKey(error));
    } finally {
      setOperation(null);
    }
  }

  async function remove(kind: ConferenceBrandingAssetKind) {
    if (
      !isAdmin ||
      !currentAsset(conference, kind) ||
      operation ||
      inspectingKind
    )
      return;
    setOperation({ kind, action: "remove" });
    setKindFeedback(kind, null);
    try {
      const brandingOverride = await client.removeConferenceBranding(
        conference.conferenceId,
        kind,
      );
      onBrandingUpdated(brandingOverride);
      clearSelection(kind);
      setKindFeedback(kind, "branding.removed");
    } catch (error) {
      setKindFeedback(kind, mutationErrorKey(error));
    } finally {
      setOperation(null);
    }
  }

  return (
    <section
      className="detail-panel conference-branding"
      aria-labelledby="conference-branding-title"
    >
      <header className="conference-branding__heading">
        <div>
          <h2 id="conference-branding-title">{t("branding.title")}</h2>
          <p>{t("branding.description")}</p>
        </div>
        <span className="conference-branding__access">
          {isAdmin ? t("branding.access.admin") : t("branding.access.readOnly")}
        </span>
      </header>
      <div className="conference-branding__grid">
        {(["icon", "logo"] as const).map((kind) => {
          const asset = currentAsset(conference, kind);
          const selected = selections[kind];
          const busy = operation !== null || inspectingKind !== null;
          const thisOperation = operation?.kind === kind ? operation : null;
          const inputId =
            `conference-branding-${conference.conferenceId}-${kind}`.replace(
              /[^a-zA-Z0-9_-]/g,
              "-",
            );
          return (
            <form
              className="conference-branding__asset"
              key={kind}
              onSubmit={(event) => void upload(kind, event)}
            >
              <BrandingAssetPreview
                conference={conference}
                kind={kind}
                previewUrl={previews[kind]}
              />
              <div className="conference-branding__asset-copy">
                <h3>{assetLabel(kind, t)}</h3>
                <p>{assetDescription(kind, t)}</p>
                <p className="conference-branding__version">
                  {versionSummary(asset, t, number)}
                </p>
                {selected ? (
                  <p className="conference-branding__selection">
                    {t("branding.readyToUpload", {
                      size: isolateLtr(
                        number(selected.size / 1_048_576, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                      ),
                    })}
                  </p>
                ) : null}
              </div>
              {isAdmin ? (
                <div className="conference-branding__controls">
                  <input
                    className="visually-hidden"
                    id={inputId}
                    type="file"
                    accept={CONFERENCE_BRANDING_ACCEPT}
                    disabled={busy}
                    onChange={(event) => void selectFile(kind, event)}
                  />
                  <label
                    className="button button--secondary button--compact"
                    htmlFor={inputId}
                    aria-disabled={busy}
                  >
                    {t("branding.chooseImage")}
                  </label>
                  <button
                    className="button button--compact"
                    type="submit"
                    disabled={busy || !selected}
                  >
                    {thisOperation?.action === "upload"
                      ? t("branding.uploading")
                      : t("branding.upload")}
                  </button>
                  {asset ? (
                    <button
                      className="button button--danger button--compact"
                      type="button"
                      disabled={busy}
                      onClick={() => void remove(kind)}
                    >
                      {thisOperation?.action === "remove"
                        ? t("branding.removing")
                        : t("branding.removeOverride")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <p className="conference-branding__feedback" aria-live="polite">
                {feedback[kind]
                  ? t(feedback[kind], {
                      asset: assetLabel(kind, t),
                      ...limitParameters,
                    })
                  : "\u00a0"}
              </p>
            </form>
          );
        })}
      </div>
      <p className="conference-branding__constraints">
        {t("branding.constraints", limitParameters)}
      </p>
      {!isAdmin ? (
        <p className="conference-branding__viewer-note">
          {t("branding.viewerNote")}
        </p>
      ) : null}
    </section>
  );
}
